/**
 * The hatch material library data and procedural fill functions.
 * Async loading functions are in gfx/hatchMaterialLoader.ts to avoid circular deps.
 * @module
 */
import { aabb2Height, aabb2Width } from "geom/aabb2";
import type { AABB2 } from "geom/aabb2Types";
import { DIST_EPSILON } from "geom/core";
import type { ProceduralHatchFunction } from "geom/hatchTypes";
import { HatchMaterialType } from "geom/hatchTypes";
import { polyLine2ByTwoPoints } from "geom/polyLine2";
import type { PolyLine2 } from "geom/polyLine2Types";
import { pushArray } from "geom/utils";
import { HatchMaterialId } from "./hatchMaterialId";

interface HatchProceduralMaterialSrc {
  type: HatchMaterialType.PROCEDURAL;
  fillFunction: ProceduralHatchFunction;
  sizeU: number;
  sizeV: number;
}

interface HatchPatternMaterialSrc {
  type: HatchMaterialType.PATTERN;
  hatchMaterialJSON: string;
}

type HatchMaterialSrc = HatchProceduralMaterialSrc | HatchPatternMaterialSrc;

export function isLibraryHatchMaterial(hatchMaterialId: string): boolean {
  return hatchMaterialId in HatchMaterialId;
}

function hatchProceduralMaterialSrcNew(
  fillFunction: ProceduralHatchFunction,
  sizeU: number,
  sizeV: number,
): HatchProceduralMaterialSrc {
  return {
    type: HatchMaterialType.PROCEDURAL,
    fillFunction,
    sizeU,
    sizeV,
  };
}

function hatchPatternMaterialSrcNew(hatchMaterialJSON: string): HatchPatternMaterialSrc {
  return {
    type: HatchMaterialType.PATTERN,
    hatchMaterialJSON,
  };
}

/**
 * These values are hardcoded into the hatch pattern as PDF_DPI line offsets.
 * We don't currently let users modify this, but if we do the values should be in
 * SheetInches
 */
export const DIAGONAL_HATCH_SEP_PDF_DPI = 12;
export const GRID_HATCH_SEP_PDF_DPI = 9;
const DEFAULT_SIZE = 1;

const hatchMaterialLibrary: { [name in HatchMaterialId]: HatchMaterialSrc } = {
  None: hatchProceduralMaterialSrcNew(emptyHatchFillfunc, DEFAULT_SIZE, DEFAULT_SIZE),
  Debug_Ground: hatchProceduralMaterialSrcNew(debugGroundHatchFillFunc, 100, 100),
  Bricks: hatchPatternMaterialSrcNew("/static/hatchMaterials/Bricks.json"),
  Bricks_Vertical: hatchProceduralMaterialSrcNew(brickSoldierCourseFillFunc, 2.5, DEFAULT_SIZE),
  Bricks_Drunken_Soldier: hatchProceduralMaterialSrcNew(drunkenSoldierHatchFillFunc, 4, DEFAULT_SIZE),
  Wood_Floor: hatchPatternMaterialSrcNew("/static/hatchMaterials/Wood_Floor.json"),
  Shakes: hatchPatternMaterialSrcNew("/static/hatchMaterials/Shakes.json"),
  Scales: hatchPatternMaterialSrcNew("/static/hatchMaterials/Scales.json"),
  Stone: hatchPatternMaterialSrcNew("/static/hatchMaterials/Stone.json"),
  Stucco: hatchPatternMaterialSrcNew("/static/hatchMaterials/Stucco.json"),
  Vertical_Lines: hatchProceduralMaterialSrcNew(verticalLineHatchFillFunc, 10, DEFAULT_SIZE),
  Diagonal_Lines: hatchProceduralMaterialSrcNew(diagonalLineHatchFillFunc, 8, 8),
  Section_Lines: hatchProceduralMaterialSrcNew(sectionLineHatchFillFunc, 2, 2),
  Diagonal_CrossHatch: hatchProceduralMaterialSrcNew(diagonalCrossHatchFillFunc, 4, 4),
  Horizontal_Lines: hatchProceduralMaterialSrcNew(horizontalLineHatchFillFunc, DEFAULT_SIZE, 10),
  Roof_Asphalt: hatchPatternMaterialSrcNew("/static/hatchMaterials/Roof_Asphalt.json"),
  RoofTile_Capistrano: hatchPatternMaterialSrcNew("/static/hatchMaterials/RoofTile_Capistrano.json"),
  Gravel: hatchPatternMaterialSrcNew("/static/hatchMaterials/Gravel.json"),
  Wood_Wall: hatchPatternMaterialSrcNew("/static/hatchMaterials/Wood_Wall.json"),
  Vertical_Wood_Wall: hatchPatternMaterialSrcNew("/static/hatchMaterials/Vertical_Wood_Wall.json"),
  Metal_Roof: hatchPatternMaterialSrcNew("/static/hatchMaterials/Metal_Roof.json"),
  Glass: hatchPatternMaterialSrcNew("/static/hatchMaterials/Glass.json"),
  Metal_Screen: hatchPatternMaterialSrcNew("/static/hatchMaterials/Metal_Screen.json"),
  Annotation_Diagonal_Down: hatchProceduralMaterialSrcNew(
    (aabb) => diagonalLineHatchFillFunc(aabb, DIAGONAL_HATCH_SEP_PDF_DPI),
    DIAGONAL_HATCH_SEP_PDF_DPI,
    DIAGONAL_HATCH_SEP_PDF_DPI,
  ),
  Annotation_Diagonal_Up: hatchProceduralMaterialSrcNew(
    (aabb) => diagonalLineHatchFillFunc(aabb, DIAGONAL_HATCH_SEP_PDF_DPI, true),
    DIAGONAL_HATCH_SEP_PDF_DPI,
    DIAGONAL_HATCH_SEP_PDF_DPI,
  ),
  Annotation_CrossHatch: hatchProceduralMaterialSrcNew(
    (aabb) => diagonalCrossHatchFillFunc(aabb, DIAGONAL_HATCH_SEP_PDF_DPI),
    DIAGONAL_HATCH_SEP_PDF_DPI,
    DIAGONAL_HATCH_SEP_PDF_DPI,
  ),
  Annotation_Grid: hatchProceduralMaterialSrcNew(
    (aabb) => gridHatchFillFunc(aabb, GRID_HATCH_SEP_PDF_DPI),
    GRID_HATCH_SEP_PDF_DPI,
    GRID_HATCH_SEP_PDF_DPI,
  ),
};

function horizontalLineHatchFillFunc(aabb2: AABB2, sep: number = 10.0): PolyLine2[] {
  const lines: PolyLine2[] = [];

  const { minX, minY, maxX, maxY } = aabb2;

  const startY = Math.floor(minY / sep) * sep;
  const endY = Math.ceil(maxY / sep) * sep + DIST_EPSILON;
  for (let y = startY; y <= endY; y += sep) {
    lines.push(polyLine2ByTwoPoints(minX, y, maxX, y));
  }

  return lines;
}

function verticalLineHatchFillFunc(aabb2: AABB2, sep: number = 10.0): PolyLine2[] {
  const lines: PolyLine2[] = [];

  const { minX, minY, maxX, maxY } = aabb2;

  const startX = Math.floor(minX / sep) * sep;
  const endX = Math.ceil(maxX / sep) * sep + DIST_EPSILON;
  for (let x = startX; x <= endX; x += sep) {
    lines.push(polyLine2ByTwoPoints(x, minY, x, maxY));
  }

  return lines;
}

function sectionLineHatchFillFunc(aabb2: AABB2): PolyLine2[] {
  return diagonalLineHatchFillFunc(aabb2, 2);
}

function gridHatchFillFunc(aabb2: AABB2, sep: number = 10.0): PolyLine2[] {
  const lines = horizontalLineHatchFillFunc(aabb2, sep);
  pushArray(lines, verticalLineHatchFillFunc(aabb2, sep));
  return lines;
}

function diagonalLineHatchFillFunc(aabb2: AABB2, sep: number = 8.0, flip = false): PolyLine2[] {
  const lines: PolyLine2[] = [];

  const axisAlignedSep = Math.SQRT2 * sep;

  const { minY, maxX, minX } = aabb2;

  const ay = minY;
  const bx = flip ? minX : maxX;
  let ax = flip ? minX + axisAlignedSep : maxX - axisAlignedSep;
  let by = minY + axisAlignedSep;

  const width = aabb2Width(aabb2);
  const height = aabb2Height(aabb2);

  const domain = Math.max(width, height) + Math.min(width, height) * Math.SQRT2;

  for (let x = 0; x <= domain; x += sep) {
    lines.push(polyLine2ByTwoPoints(ax, ay, bx, by));

    ax += flip ? sep : -sep;
    by += sep;
  }

  return lines;
}

function diagonalCrossHatchFillFunc(aabb2: AABB2, sep: number = 4.0): PolyLine2[] {
  const lines = diagonalLineHatchFillFunc(aabb2, sep);
  pushArray(lines, diagonalLineHatchFillFunc(aabb2, sep, true));
  return lines;
}

function brickSoldierCourseFillFunc(aabb2: AABB2, sep: number = 2.5): PolyLine2[] {
  const lines: PolyLine2[] = [];

  const { minX, minY, maxX, maxY } = aabb2;

  const startX = Math.floor(minX / sep) * sep;
  const endX = Math.ceil(maxX / sep) * sep + DIST_EPSILON;
  for (let x = startX; x <= endX; x += sep) {
    lines.push(polyLine2ByTwoPoints(x, minY, x, maxY));
  }

  return lines;
}

function drunkenSoldierHatchFillFunc(aabb2: AABB2, sep: number = 4.0): PolyLine2[] {
  // generates a mirrored pattern like so:
  // \\\\\\\\ /////////
  // (drunken soldier = bricks soldier, but leaning at an angle)

  const lines: PolyLine2[] = [];

  const { minX, minY, maxX, maxY } = aabb2;

  const startX = Math.floor(minX / sep) * sep;
  const endX = Math.ceil(maxX / sep) * sep + DIST_EPSILON;

  for (let x = startX; x <= endX; x += sep) {
    const percent = (x - startX) / (endX - startX);
    const offset = sep * 0.35;
    lines.push(polyLine2ByTwoPoints(percent < 0.5 ? x + offset : x - offset, minY, x, maxY));
  }

  return lines;
}

function debugGroundHatchFillFunc(aabb2: AABB2, sep: number = 100.0): PolyLine2[] {
  const lines: PolyLine2[] = [];

  const { minX, minY, maxX, maxY } = aabb2;

  const startX = Math.floor(minX / sep) * sep;
  const startY = Math.floor(minY / sep) * sep;
  const endX = Math.ceil(maxX / sep) * sep + DIST_EPSILON;
  const endY = Math.ceil(maxY / sep) * sep + DIST_EPSILON;

  for (let x = startX; x <= endX; x += sep) {
    lines.push(polyLine2ByTwoPoints(x, minY, x, maxY));
  }

  for (let y = startY; y <= endY; y += sep) {
    lines.push(polyLine2ByTwoPoints(minX, y, maxX, y));
  }

  return lines;
}

function emptyHatchFillfunc(_aabb2: AABB2): PolyLine2[] {
  return [];
}

export default hatchMaterialLibrary;
